/**
 * 008-reporting-dashboards T021 (FR-014, constitution V). This is the FIRST staleness screen built
 * in `apps/admin`, not a second one, verified against `nav.tsx` (`/staleness` was `built: false`)
 * and `dashboard.tsx`'s own stale-tier-prices tile (which said "isn't built here yet" until this
 * task). "Consistent with `001`'s staleness screen" (FR-014) has nothing to be consistent with yet
 * — this reuses `TierStalenessRow`/`staleReasonLabel` from `lib/services/catalog.ts` (built for
 * `001`'s dashboard tile), which is the one existing implementation to build on.
 *
 * `GET /api/catalog/reports/tier-staleness` has no from/to (`dateField` is always `"none"` — a
 * statement about the catalogue as it stands, see `catalog.ts`'s own note) but IS server-paged
 * (`tierId`/`reason`/`page`/`pageSize` query params) — the one existing server-paged list pattern in
 * this app, `PagedTable` (`components/table/data-table.tsx`), rather than the unpaged
 * fetch-after-mount shape `StockMovement.tsx`/`Variance.tsx`/`OrderToInvoice.tsx` use. That means a
 * route loader, like `orders/List.tsx` — no component-level `can()` check either, same as that file:
 * the root loader (`pages/root.tsx`) already refuses the route via `permissionForPath`/`nav.tsx`'s
 * `staleness.read`-gated entry, now pointed at this route instead of the unbuilt placeholder.
 *
 * The CSV export is a plain link to `tierStalenessReportCsvUrl`'s `?format=csv` URL, not the shared
 * `ExportButton` — this is the one server-paged report in this folder (`export-guard.test.ts`'s own
 * header comment), and `ExportButton` only ever exports a client-held array, which here would be one
 * page's rows dressed up as a full export. The link carries the same `tierId`/`reason` filters the
 * screen is showing, and the backend returns every matching row in one response, unpaged.
 */
import { useState } from "react";
import { useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { reapplyPreviewPath } from "../../lib/paths";
import { PiArrowLineUpBold } from "react-icons/pi";
import { Alert, Badge, Button, Flex, Text } from "rizzui";
import { CheckboxCard } from "../../components/checkbox-card";
import { EmptyState, ErrorSurface, StatusField, WidgetCard } from "@steelyard/theme";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { FILTER_LABEL } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { getStalenessSummary, getTierStalenessReport, listTiers, previewReapply, tierStalenessReportCsvUrl } from "../../lib/services/catalog";
import { formatMoment, sentenceCase } from "../../lib/format";
import { hrefWith, readPaging } from "../../lib/paging";
import { tierStalenessColumns } from "./tier-staleness-columns";
import type { AccountSummary, PermissionState } from "../../types";

const REASON_OPTIONS = [
  { value: "base_price_moved", label: "Base price moved" },
  { value: "newly_added", label: "Newly added" },
  { value: "rule_changed", label: "Discount rule changed" },
];

export async function stalenessReportLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const tierId = params.get("tierId") ?? undefined;
  const reason = params.get("reason") ?? undefined;
  // Every tier, active or not — a deactivated tier's prices can still be stale. Names for the Tier
  // select only, so a failed read leaves the select empty rather than taking the report down with it.
  // The reason options' counts are the catalogue-wide summary's, so they are read only when no tier is
  // selected — a tier-filtered list must not show catalogue-wide numbers as its own.
  const [report, tiers, summary] = await Promise.all([
    getTierStalenessReport({ tierId, reason, page, pageSize }),
    listTiers({ page: 1, pageSize: 200 }).catch(() => null),
    tierId ? Promise.resolve(null) : getStalenessSummary().catch(() => null),
  ]);
  return { report, tierId, reason, summary, tierOptions: (tiers?.items ?? []).map((tier) => ({ value: tier.id, label: tier.name })) };
}

export default function StalenessReportPage() {
  const { report, tierId, reason, summary, tierOptions } = useLoaderData() as Awaited<ReturnType<typeof stalenessReportLoader>>;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  // 001-catalogue-admin-screens T077 (US4, FR-023): opt-in re-apply preview for the tier this
  // screen is filtered to. `includeManuallySet` defaults false — hand-edited rows stay excluded
  // unless explicitly asked for, same default the backend itself applies when the body is omitted.
  const [includeManuallySet, setIncludeManuallySet] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);

  async function onPreviewReapply() {
    if (!tierId) return;
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const preview = await previewReapply(tierId, includeManuallySet);
      navigate(reapplyPreviewPath(preview.previewNumber || preview.previewId));
    } catch (err) {
      setPreviewError(err);
    } finally {
      setPreviewBusy(false);
    }
  }

  const query = (over: Record<string, string | undefined>) => hrefWith("/reports/staleness", searchParams, over);
  // Every filter change is a different result set: back to its first page.
  const apply = (over: { tierId?: string; reason?: string }) => navigate(query({ page: undefined, ...over }));

  const filtered = Boolean(tierId || reason);
  const tierName = tierOptions.find((option) => option.value === tierId)?.label ?? "this tier";
  const mayReapply = can(permissionState, "pricing.administer");

  const reasonOptions: StatusOption[] = [
    { label: "All reasons", href: query({ reason: undefined, page: undefined }), active: !reason, count: summary?.staleCount ?? null },
    ...REASON_OPTIONS.map((option) => ({
      label: option.label,
      href: query({ reason: option.value, page: undefined }),
      active: option.value === reason,
      // A reason the API did not report has no count — omitted, never an invented 0.
      count: summary?.byReason[option.value] ?? null,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Tier Price Staleness"
        count={report.rows.totalCount}
        description={`Customer-tier prices that no longer match today's base price or their tier's discount rules. Rows whose base price moved more than ${report.varianceThresholdPct}% are marked critical. Snapshot at ${formatMoment(report.producedAt)}.`}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Pricing" }, { name: "Stale Prices" }]}
      >
        {/* The API's own count of critical rows under the same filters — never counted here. */}
        <Badge variant="flat" color={report.criticalCount > 0 ? "danger" : "secondary"} rounded="md">
          {report.criticalCount} critical
        </Badge>
      </ListHeader>

      {/* What a reader kept asking: why is this here, what are customers paying, and what next. */}
      <Alert color="info" bar>
        <p className="font-semibold">How stale prices work</p>
        <ol className="mt-1 list-decimal ps-5">
          <li>
            <b>Something changed.</b> A SKU&apos;s base price was edited, a tier&apos;s discount rules were edited, or a SKU
            got its first base price. Every tier price that depends on it is listed below.
          </li>
          <li>
            <b>Customers are not affected yet.</b> <span>{report.orderingImpact}</span> Customers on that tier keep paying the
            &ldquo;Customers pay now&rdquo; rate.
          </li>
          <li>
            <b>You decide when to update.</b> Choose a tier, preview its new prices, then commit. Only that tier&apos;s
            customers move to the new prices; every other tier stays as it is until you do the same for it.
          </li>
        </ol>
      </Alert>

      {/* T077 (FR-022/FR-023): re-apply is per-tier — the backend route is `POST
          /tiers/{id}/reapply/preview` — so the button only appears once the screen is filtered to
          one tier; before that the card says how to get there. `pricing.administer` is separate
          from the `pricing.read` this route requires; a reader without it never sees the card. */}
      {mayReapply ? (
        <WidgetCard title={tierId ? `Update ${tierName}'s prices` : "Update a tier's prices"}>
          {tierId ? (
            <>
              {previewError ? <ErrorSurface error={previewError} action="preview a re-apply" /> : null}
              <Text className="mb-4 text-sm">
                Recalculates every rule-based price on {tierName} from today&apos;s base prices and {tierName}&apos;s discount
                rules. The next screen shows each old and new price side by side — nothing changes until you commit there.
              </Text>
              <Flex align="center" gap="4" className="w-auto flex-wrap">
                <CheckboxCard
                  label="Include manually-set prices"
                  helperText="Off by default — a price someone typed in by hand stays as it is unless you tick this."
                  checked={includeManuallySet}
                  onChange={(event) => setIncludeManuallySet(event.target.checked)}
                />
                <Button onClick={onPreviewReapply} isLoading={previewBusy} disabled={previewBusy}>
                  {previewBusy ? "Building preview…" : "Preview re-apply"}
                </Button>
              </Flex>
            </>
          ) : (
            <Text className="text-sm">
              Prices are updated one tier at a time. Choose a tier in the <b>Tier</b> filter below to preview its new
              prices.
            </Text>
          )}
        </WidgetCard>
      ) : null}

      {/* Always the grid, so the filters stay reachable when they narrow the report to nothing. */}
      <PagedTable
        actions={
          report.rows.totalCount > 0 ? (
            <a href={tierStalenessReportCsvUrl({ tierId, reason })} download={`tier-staleness${tierId ? `-${(tierOptions.find((option) => option.value === tierId)?.label ?? "tier").replace(/\s+/g, "-")}` : ""}.csv`}>
              <Button as="span" variant="outline" className="w-full @lg:w-auto">
                <PiArrowLineUpBold className="me-1.5 h-[17px] w-[17px]" />
                Export
              </Button>
            </a>
          ) : null
        }
        result={report.rows}
        caption={`Stale tier prices, past ${report.varianceThresholdPct}% drift from base rate — ${report.criticalCount} critical`}
        columns={tierStalenessColumns(can(permissionState, "inventory.read"), mayReapply, Boolean(tierId))}
        getRowId={(row) => row.tierPriceId}
        // The API's own `critical` flag, marked down the row's leading edge so it reads before any figure.
        // `!`: the table variant's own row border colour otherwise wins over a single row's.
        rowClassName={(row) => (row.critical ? "border-l-4 !border-l-red" : undefined)}
        status={{ label: "Reason", options: reasonOptions }}
        filters={
          <StatusField
            label="Tier"
            labelClassName={FILTER_LABEL}
            placeholder="Every tier"
            options={tierOptions}
            value={tierOptions.find((option) => option.value === tierId) ?? null}
            onChange={(option: { value: string } | null) => apply({ tierId: option?.value })}
            clearable
            onClear={() => apply({ tierId: undefined })}
            dropdownClassName="!z-10 h-auto"
            className="w-full @5xl:w-56"
          />
        }
        clear={{ active: filtered, href: query({ tierId: undefined, reason: undefined, page: undefined }) }}
        empty={
          filtered ? (
            <EmptyState
              title="No rows matched"
              action={
                <Button size="sm" variant="outline" onClick={() => apply({ tierId: undefined, reason: undefined })}>
                  Clear the filters
                </Button>
              }
            >
              Nothing matched those filters. Clear them to see every stale tier price.
            </EmptyState>
          ) : (
            <EmptyState title="No stale tier prices">
              {/* A reason that just restates the title above ("No stale tier prices.") adds nothing
                  — only show it when it says something the title doesn't. */}
              {report.emptyReason && sentenceCase(report.emptyReason) !== "No stale tier prices"
                ? sentenceCase(report.emptyReason) + "."
                : "No tier price has drifted from its base price."}
            </EmptyState>
          )
        }
      />
    </div>
  );
}
